import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { BetslipType } from '../../../core/betslip-type';
import BetslipComboBetActions from '../../combo-bet/actions';
import { selectCurrentInfoMessageId } from '../../info-messages/selectors';
import { ensureLinearMode } from '../../linear/helpers';
import BetslipPickActions from '../../picks/actions';
import { RewardTokensActions } from '../../reward-tokens/actions';
import { IRewardToken, RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { selectRewardTokens } from '../../reward-tokens/selectors';
import DigitalSettingsActions from '../../settings/digital-actions';
import { BetslipStakeActions } from '../../stake/actions';
import BetslipSystemBetActions from '../../system-bet/actions';
import BetslipErrorsActions from '../../validation/actions';
import { LinearCheckboxTrackingState, LinearCtaTrackingType, LinearModuleExpansionTrackingState } from '../models';
import { BetslipLinearTrackingService } from '../services/betslip-linear-tracking.service';
import LinearBetslipTrackingActions from './linear-tracking.actions';

@Injectable()
export default class BetslipLinearTrackingEffects {
    constructor(
        private actions$: Actions,
        protected betslipLinearTrackingService: BetslipLinearTrackingService,
        private store: Store,
    ) {}

    handleLoadBetslipEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.loadBetslip),
                tap(() => this.betslipLinearTrackingService.trackBetslipLoad()),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleClickInfoIconEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.clickInfoIcon),
                concatLatestFrom(() => this.store.select(selectCurrentInfoMessageId)),
                filter(([action, currentMessageId]) => action.messageId === currentMessageId),
                tap(([action]) => this.betslipLinearTrackingService.trackInfoIconClick(action.infoContext, action.additionalTypeContext)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handlePickCheckboxEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.togglePickForType),
                tap<ReturnType<typeof BetslipPickActions.togglePickForType>>((action) =>
                    this.betslipLinearTrackingService.trackCheckboxToggle(
                        action.betslipType,
                        action.newSelectionState ? LinearCheckboxTrackingState.Selected : LinearCheckboxTrackingState.Unselected,
                    ),
                ),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handlePickListExpansionTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipComboBetActions.linearPickListVisibilitySwitch),
                tap((action) => this.betslipLinearTrackingService.trackPickListVisibilitySwitch(action.positionEvent, action.eventDetails)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleModuleExpansionToggleEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.toggleModuleExpansion),
                tap<ReturnType<typeof LinearBetslipTrackingActions.toggleModuleExpansion>>((action) =>
                    this.betslipLinearTrackingService.trackModuleExpansionToggle(
                        action.betslipType,
                        action.isExpanded ? LinearModuleExpansionTrackingState.Expand : LinearModuleExpansionTrackingState.Collapse,
                    ),
                ),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSystemBetTypeStakeClickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipSystemBetActions.clickSystemTypeStake),
                tap((action) =>
                    this.betslipLinearTrackingService.trackSytemBetTypeStakeClick(`stake - ${action.stakeAmount ?? 0} - ${action.betType.key}`),
                ),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleStakeClickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipStakeActions.trackStakeClick),
                tap((action) => this.betslipLinearTrackingService.trackStakeClick(action.betslipType, `stake - ${action.stakeAmount ?? 0}`)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSystemBetBankerClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.clickSystemBetBanker),
                tap<ReturnType<typeof LinearBetslipTrackingActions.clickSystemBetBanker>>((action) =>
                    this.betslipLinearTrackingService.trackSystemBankerBetClick(action.pick),
                ),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleBetslipClientErrorsTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipErrorsActions.trackBetslipClientErrors),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                filter(([, isLinear]) => isLinear),
                tap(([action]) => {
                    this.betslipLinearTrackingService.trackBetslipClientErrors(action.trackingData);
                }),

                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackAddPromoClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.openDialog),
                ensureLinearMode(this.store),
                tap<ReturnType<typeof RewardTokensActions.openDialog>>((action) => {
                    this.betslipLinearTrackingService.trackAddPromotionsClick(action.tokenContext.betslipType);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleLoadPromotionsPopupEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.loadPromotionsPopup),
                tap<ReturnType<typeof LinearBetslipTrackingActions.loadPromotionsPopup>>((action) =>
                    this.betslipLinearTrackingService.trackPromotionsPopupLoad(action.betslipType),
                ),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackClosePromotionsPopupEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ensureLinearMode(this.store),
                ofType(RewardTokensActions.closeDialog),
                map((action) => 'tokenContext' in action && action.tokenContext),
                filter((tokenContext): tokenContext is RewardTokenContext => !!tokenContext),
                tap<RewardTokenContext>((tokenContext) => {
                    this.betslipLinearTrackingService.trackClosePromotionsPopupClick(tokenContext.betslipType);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackTokenSelectionClick$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.toggleToken),
                ensureLinearMode(this.store),
                concatLatestFrom(() => this.store.select(selectRewardTokens)),
                map(([action, tokens]) => {
                    const existingToken = tokens[action.payload.tokenId];

                    return [action.payload.betslipType, existingToken] as [BetslipType, IRewardToken];
                }),
                filter(([, token]) => !!token),
                tap<[BetslipType, IRewardToken]>(([betslipType, token]) => {
                    this.betslipLinearTrackingService.trackTokenSelectionClick(betslipType, token);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSettingsOpenClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(DigitalSettingsActions.openSettings),
                ensureLinearMode(this.store),
                tap(() => this.betslipLinearTrackingService.trackSettingsIconClick()),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleLoadSettingsEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.loadSettings),
                ensureLinearMode(this.store),
                tap(() => this.betslipLinearTrackingService.trackSettingsLoad()),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSettingsCloseClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(DigitalSettingsActions.closeSettings),
                filter((action: Action<string> & { trackEvent?: boolean }) => !!action.trackEvent),
                ensureLinearMode(this.store),
                tap(() => this.betslipLinearTrackingService.trackSettingsCloseClick()),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleClickBetslipCloseEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.clickBetslipCloseCta),
                tap(() => this.betslipLinearTrackingService.trackBetslipCtaClick(LinearCtaTrackingType.CloseCta)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleClickBetslipDepositEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LinearBetslipTrackingActions.clickBetslipDepositCta),
                tap(() => this.betslipLinearTrackingService.trackBetslipCtaClick(LinearCtaTrackingType.DepositCta)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
